import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import type { CollectOption, ShippingOption } from '@alwindows/shared/api/spareParts'

/**
 * In-session shipment state for the Cart → Checkout transition. NOT persisted —
 * deliberately separate from the localStorage cart, which only carries line items.
 *
 * Why context (not Redux): only two pages touch this; no cross-tab sync needed;
 * lifetime is the active SPA session.
 */

export type ShipmentMethod = 'Pickup' | 'Delivery'

export type ShipmentSelection =
  | { method: 'Pickup'; option: CollectOption }
  | {
      method: 'Delivery'
      packagingOption: ShippingOption
      auspostService: { code: string; name: string; price: string }
    }

type Ctx = {
  selection: ShipmentSelection | null
  setSelection: (sel: ShipmentSelection | null) => void
  clear: () => void
}

const ShipmentContext = createContext<Ctx | null>(null)

export function ShipmentProvider({ children }: { children: ReactNode }) {
  const [selection, setSelection] = useState<ShipmentSelection | null>(null)
  const clear = useCallback(() => setSelection(null), [])
  return (
    <ShipmentContext.Provider value={{ selection, setSelection, clear }}>
      {children}
    </ShipmentContext.Provider>
  )
}

export function useShipment(): Ctx {
  const ctx = useContext(ShipmentContext)
  if (!ctx) throw new Error('useShipment must be used inside <ShipmentProvider>')
  return ctx
}
